import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, ExternalLink, TrendingUp } from 'lucide-react';

const caseStudies = [
  {
    id: 1,
    title: 'E-Commerce Platform Redesign',
    client: 'Fashion Retailer',
    category: 'E-Commerce',
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166',
    description: 'Complete redesign of an e-commerce platform resulting in significant improvements in conversions and user engagement.',
    challenge: 'The client had an outdated website with poor mobile experience and low conversion rates.',
    solution: 'We redesigned the entire shopping experience with a mobile-first approach, improved checkout flow, and enhanced product discovery.',
    results: [
      { metric: '150%', label: 'Increase in Conversions' },
      { metric: '40%', label: 'Improved Engagement' },
      { metric: '60%', label: 'Mobile Traffic Growth' },
      { metric: '25%', label: 'Reduced Bounce Rate' }
    ],
    tags: ['UI/UX', 'Development', 'SEO']
  },
  {
    id: 2,
    title: 'SaaS Dashboard Application',
    client: 'Tech Startup',
    category: 'Web Application',
    image: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613',
    description: 'Custom analytics dashboard with real-time data visualization and intuitive user interface.',
    challenge: 'The startup needed a powerful yet user-friendly analytics platform to serve their B2B customers.',
    solution: 'We built a custom dashboard with real-time data processing, interactive charts, and role-based access control.',
    results: [
      { metric: '10K+', label: 'Active Users' },
      { metric: '99.9%', label: 'System Uptime' },
      { metric: '4.8/5', label: 'User Satisfaction' },
      { metric: '50%', label: 'Time Saved' }
    ],
    tags: ['Custom Tool', 'UI/UX', 'Development']
  },
  {
    id: 3,
    title: 'Brand Identity & Website',
    client: 'Professional Services',
    category: 'Branding',
    image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085',
    description: 'Complete brand overhaul including logo, website, and digital marketing strategy.',
    challenge: 'An established firm needed to modernize their brand and improve their digital presence to attract younger clients.',
    solution: 'We developed a fresh brand identity and built a modern website with integrated lead generation tools.',
    results: [
      { metric: '200%', label: 'Traffic Increase' },
      { metric: '85%', label: 'More Leads' },
      { metric: '3x', label: 'Social Engagement' },
      { metric: '40%', label: 'Younger Demographics' }
    ],
    tags: ['Branding', 'Development', 'Marketing']
  }
];

export default function CaseStudiesPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              <TrendingUp className="h-4 w-4 mr-2" />
              Case Studies
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              Real Results from{' '}
              <span className="gradient-text">Real Projects</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Explore our portfolio of successful projects and see how we've helped businesses like yours achieve their digital goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="space-y-24">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Image */}
                  <div className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="rounded-2xl overflow-hidden">
                      <img
                        src={study.image}
                        alt={study.title}
                        className="w-full h-auto object-cover aspect-[4/3]"
                      />
                    </div>
                    <div className="absolute -bottom-4 -right-4 w-32 h-32 gradient-primary rounded-2xl opacity-30 blur-xl" />
                  </div>

                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <Badge variant="secondary" className="mb-4">
                      {study.category}
                    </Badge>
                    <h2 className="text-3xl font-display font-bold text-foreground mb-4">
                      {study.title}
                    </h2>
                    <p className="text-sm text-muted-foreground mb-4">
                      Client: {study.client}
                    </p>
                    <p className="text-muted-foreground mb-6">
                      {study.description}
                    </p>

                    {/* Results Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {study.results.map((result, i) => (
                        <div key={i} className="bg-accent/50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-display font-bold gradient-text">
                            {result.metric}
                          </div>
                          <div className="text-xs text-muted-foreground">{result.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {study.tags.map((tag, i) => (
                        <span key={i} className="text-xs bg-muted px-3 py-1 rounded-full text-muted-foreground">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-lg text-primary-foreground/80 mb-8">
              Let's discuss your project and create results that matter.
            </p>
            <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
              <Link to="/get-started">
                Start Your Project
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
